import {
  Button,
  Skeleton,
  Box,
  Grid,
  Flex,
  Code,
  Tag,
  HStack,
  StackDivider,
  VStack,
  Heading,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Select,
  Text,
} from '@chakra-ui/react'
import { FC, useEffect, useMemo, useState } from 'react'
import { FaCubes, FaMicrochip, FaMemory } from 'react-icons/fa'
import { useMutation, gql } from '@apollo/client'

const UPDATE_PROCESS_RESOURCES = gql`
  mutation UpdateProcessResources(
    $idProcess: String!
    $instanceCount: Int
    $cpuLimit: Int
    $memoryLimit: Int
  ) {
    updateProcess(
      id: $idProcess
      instanceCount: $instanceCount
      cpuLimit: $cpuLimit
      memoryLimit: $memoryLimit
    ) {
      id
    }
  }
`

type Props = {
  isLoaded: boolean
  process: {
    id: string
    key: string
    isMain?: boolean
    entrypoint: string
    instanceCount: number
    cpuLimit: number
    memoryLimit: number
  }
  refetchData: () => void
}

const initialValues = {
  instanceCount: 0,
  cpuLimit: 0,
  memoryLimit: 0,
}

const ProcessResourceController: FC<Props> = ({
  process,
  isLoaded,
  refetchData,
}) => {
  const [state, setState] = useState(initialValues)
  const [updateProcess, { loading: isUpdating }] = useMutation(
    UPDATE_PROCESS_RESOURCES
  )

  const isTouched = useMemo(() => {
    const keys = Object.keys(state)

    // @ts-ignore
    return keys.some((key) => process[key] !== state[key])
  }, [process, state])

  useEffect(() => {
    if (Object.values(state).every((value) => value === 0) && process) {
      setState({
        instanceCount: process.instanceCount,
        cpuLimit: process.cpuLimit,
        memoryLimit: process.memoryLimit,
      })
    }
  }, [process, setState, state])

  const onReset = () => {
    setState(initialValues)
  }

  const onSave = async () => {
    const variables = {
      ...state,
      idProcess: process?.id ?? '',
    }
    await updateProcess({ variables })
    await refetchData()
  }

  return (
    <Skeleton key={`${process?.id ?? ''}`} rounded="md" isLoaded={isLoaded}>
      <Box
        p={4}
        rounded="lg"
        border="1px"
        borderColor={process?.isMain ? 'cyan.600' : 'gray.300'}
      >
        <Grid
          gridTemplateColumns="minmax(auto, 150px) auto 1fr"
          alignItems="center"
          mb={8}
        >
          <Flex alignItems="center">
            <Code mr={4} bg="none" fontWeight="bold" fontSize="md">
              {process?.key}
            </Code>
            {process?.isMain ? (
              <Tag colorScheme="cyan" mr={2}>
                Main
              </Tag>
            ) : null}
          </Flex>
          <Code rounded="md" px={1} justifySelf="start">
            {process?.entrypoint}
          </Code>
          <Flex alignItems="center" ml="auto" h="40px">
            {isTouched ? (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  mr={4}
                  onClick={onReset}
                  isDisabled={isUpdating}
                >
                  Reset
                </Button>
                <Button
                  size="sm"
                  colorScheme="cyan"
                  variant="solid"
                  onClick={onSave}
                  isLoading={isUpdating}
                >
                  Save
                </Button>
              </>
            ) : null}
          </Flex>
        </Grid>
        <HStack alignItems="center" gap={4} divider={<StackDivider />}>
          <Box flex={1}>
            <VStack alignItems="flex-start">
              <Flex alignItems="center">
                <FaCubes
                  fontSize="1.25rem"
                  style={{ marginRight: '0.75rem' }}
                />
                <Heading as="h3" size="sm">
                  Instances
                </Heading>
              </Flex>
              <Text fontSize="2xl">
                {state.instanceCount}{' '}
                {state.instanceCount > 1 ? 'instances' : 'instance'}
              </Text>
              <Slider
                colorScheme="cyan"
                aria-label="Instance count slider"
                value={state.instanceCount}
                min={1}
                max={16}
                step={1}
                onChange={(instanceCount) =>
                  setState({ ...state, instanceCount })
                }
              >
                <SliderTrack>
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb />
              </Slider>
            </VStack>
          </Box>
          <Box flex={1}>
            <VStack alignItems="flex-start">
              <Flex alignItems="center">
                <FaMicrochip
                  fontSize="1.25rem"
                  style={{ marginRight: '0.75rem' }}
                />
                <Heading as="h3" size="sm">
                  CPU
                </Heading>
              </Flex>
              <Text fontSize="2xl">
                {state.cpuLimit} {state.cpuLimit > 1 ? 'cores' : 'core'}
              </Text>
              <Slider
                colorScheme="cyan"
                aria-label="CPU core slider"
                value={state.cpuLimit}
                min={1}
                max={32}
                step={1}
                onChange={(cpuLimit) => setState({ ...state, cpuLimit })}
              >
                <SliderTrack>
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb />
              </Slider>
            </VStack>
          </Box>
          <Box flex={1} alignSelf="stretch">
            <VStack alignItems="flex-start">
              <Flex alignItems="center">
                <FaMemory
                  fontSize="1.25rem"
                  style={{ marginRight: '0.75rem' }}
                />
                <Heading as="h3" size="sm">
                  Memory
                </Heading>
              </Flex>
              <Select
                size="sm"
                colorScheme="cyan"
                value={state.memoryLimit}
                onChange={(e) =>
                  setState({ ...state, memoryLimit: parseInt(e.target.value) })
                }
                fontSize="2xl"
                variant="unstyled"
                sx={{
                  cursor: 'pointer',
                  '& ~ .chakra-select__icon-wrapper': {
                    right: 'unset',
                    left: '40%',
                  },
                }}
              >
                {Array(16)
                  .fill('')
                  .map((_, i) => 2 ** i)
                  .slice(9)
                  .map((value) => (
                    <option
                      key={value}
                      value={value}
                      style={{ fontSize: '1rem !important' }}
                    >
                      {prettyBytes(value)}
                    </option>
                  ))}
              </Select>
            </VStack>
          </Box>
        </HStack>
      </Box>
    </Skeleton>
  )
}

export default ProcessResourceController

const prettyBytes = (megabytes: number) => {
  const shouldShowMb = megabytes <= 1024
  return `${shouldShowMb ? megabytes : Math.floor(megabytes / 1000)} ${
    shouldShowMb ? 'MB' : 'GB'
  }`
}
